import { Roles } from 'consts/authConsts'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

export const useAllFeatureFlags = () => {
  const { t } = useTranslation()

  return useMemo(
    () => ({
      patientFeatureFlags: [
        {
          key: 'orthoSeenProposal',
          type: 'patient',
          label: t('featureFlags.orthoSeenProposal'),
          availableFor: [Roles.DevAdmin]
        },
        { key: 'rcSeenWelcome', type: 'patient', label: t('featureFlags.rcSeenWelcome') },
        {
          key: 'classifierAvailable',
          type: 'patient',
          label: t('featureFlags.classifierAvailable'),
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'classifierSettingsAvailable',
          type: 'patient',
          label: t('featureFlags.classifierSettingsAvailable'),
          availableFor: [Roles.DevAdmin]
        },
        {
          key: 'skipForceDetails',
          type: 'patient',
          label: t('featureFlags.skipForceDetails'),
          availableFor: [Roles.DevAdmin]
        },
        {
          key: 'medicalHistory',
          type: 'patient',
          label: t('featureFlags.medicalHistory'),
          availableFor: [Roles.DevAdmin]
        },
        { key: 'careCredit', type: 'patient', label: t('featureFlags.careCredit'), availableFor: [Roles.DevAdmin] },
        {
          key: 'preventiveDiagnosis',
          type: 'patient',
          label: t('featureFlags.preventiveDiagnosis'),
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'checkNotificationSettingsPopup_newPatients',
          type: 'patient',
          label: t('featureFlags.checkNotificationSettingsPopup_newPatients'),
          availableFor: [Roles.DevAdmin]
        },
        {
          key: 'pixelTwistScopeAvailable',
          type: 'patient',
          label: t('featureFlags.pixelTwistScopeAvailable'),
          availableFor: [Roles.Admin, Roles.Sales, Roles.Distributor]
        },
        {
          key: 'defectiveCameraDevice',
          type: 'patient',
          label: t('featureFlags.defectiveCameraDevice'),
          availableFor: [Roles.Admin, Roles.Sales, Roles.Distributor]
        },
        {
          key: 'messageModificationsAvailable',
          type: 'patient',
          label: t('featureFlags.messageModificationsAvailable'),
          availableFor: [Roles.DevAdmin]
        },
        {
          key: 'liveCallsAvailable',
          type: 'patient',
          label: t('featureFlags.liveCalls'),
          availableFor: [Roles.DevAdmin]
        },
        {
          key: 'waitForCameraReadiness',
          type: 'patient',
          label: t('featureFlags.waitForCameraReadiness'),
          availableFor: [Roles.Admin, Roles.Sales]
        }
      ].sort((a, b) => (a.label > b.label ? 1 : -1)),
      doctorFeatureFlags: [
        {
          key: 'remoteConsultation',
          type: 'doctor',
          label: t('featureFlags.remoteConsultation'),
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'ortho',
          type: 'doctor',
          label: t('featureFlags.ortho'),
          isPractice: true,
          availableFor: [Roles.DevAdmin]
        },
        {
          key: 'promotionsListA',
          type: 'doctor',
          label: t('featureFlags.promotionsListA'),
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'promotionsListB',
          type: 'doctor',
          label: t('featureFlags.promotionsListB'),
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'promotionsListC',
          type: 'doctor',
          label: t('featureFlags.promotionsListC'),
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'promotionsListD',
          type: 'doctor',
          label: t('featureFlags.promotionsListD'),
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'medicalHistory',
          type: 'doctor',
          label: t('featureFlags.medicalHistory'),
          isPractice: true,
          availableFor: [Roles.DevAdmin]
        },
        {
          key: 'scanSummaryPreventive',
          type: 'doctor',
          label: t('featureFlags.scanSummaryPreventive'),
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'autochart',
          type: 'doctor',
          label: t('featureFlags.autochart'),
          isPractice: true,
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'rotateScanVideo',
          type: 'doctor',
          label: t('featureFlags.rotateScanVideo'),
          isPractice: true,
          availableFor: [Roles.DevAdmin]
        },
        {
          key: 'exportStl',
          type: 'doctor',
          label: t('featureFlags.exportStl'),
          isPractice: true,
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        { key: 'intercom', type: 'doctor', label: t('featureFlags.intercom'), availableFor: [Roles.DevAdmin] },
        {
          key: 'oralHygieneNote',
          type: 'doctor',
          label: t('featureFlags.oralHygieneNote'),
          isPractice: true,
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'viewScanSummaryModelVersion',
          type: 'doctor',
          label: t('featureFlags.viewScanSummaryModelVersion'),
          isPractice: true,
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'treatmentTracker',
          type: 'doctor',
          label: t('featureFlags.treatmentTracker'),
          isPractice: true,
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'treatmentTrackerBraces',
          type: 'doctor',
          label: t('featureFlags.treatmentTrackerBraces'),
          isPractice: true,
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'treatmentTrackerPartialAligners',
          type: 'doctor',
          label: t('featureFlags.treatmentTrackerPartialAligners'),
          isPractice: true,
          availableFor: [Roles.DevAdmin]
        },
        {
          key: 'treatmentTrackerBetaProviders',
          type: 'doctor',
          label: t('featureFlags.treatmentTrackerBetaProviders'),
          isPractice: true,
          availableFor: [Roles.DevAdmin]
        },
        {
          key: 'treatmentTrackerJooylign',
          type: 'doctor',
          label: t('featureFlags.treatmentTrackerJooylign'),
          isPractice: true,
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'treatmentTrackerRetention',
          type: 'doctor',
          label: t('featureFlags.treatmentTrackerRetention'),
          isPractice: true,
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'recordsAppSkipEmail',
          type: 'doctor',
          label: t('featureFlags.recordsAppSkipEmail'),
          isPractice: true,
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'checkNotificationSettingsPopup_existingPatients',
          type: 'doctor',
          label: t('featureFlags.checkNotificationSettingsPopup_existingPatients'),
          availableFor: [Roles.DevAdmin]
        },
        {
          key: 'hiTools',
          type: 'doctor',
          label: t('featureFlags.hiTools'),
          isPractice: true,
          availableFor: [Roles.Admin, Roles.Sales, Roles.Distributor]
        },
        {
          key: 'hiTieredObsRetentionCopilot',
          type: 'doctor',
          label: t('featureFlags.hiTieredObsRetentionCopilot'),
          isPractice: true,
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'txTrackerTxGoals',
          type: 'doctor',
          label: t('featureFlags.txTrackerTxGoals'),
          isPractice: true,
          availableFor: [Roles.DevAdmin]
        },
        {
          key: 'overlayDrawingFF',
          type: 'doctor',
          label: t('featureFlags.overlayDrawingFF'),
          isPractice: true,
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'virtualBackgroundFF',
          type: 'doctor',
          label: t('featureFlags.virtualBackgroundFF'),
          isPractice: true,
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'practiceAutomations',
          type: 'doctor',
          label: t('featureFlags.practiceAutomations'),
          isPractice: true,
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'mfa',
          type: 'doctor',
          label: t('featureFlags.mfa'),
          isPractice: true,
          availableFor: [Roles.Admin, Roles.OpsAdmin]
        },
        {
          key: 'dsoInvitePractice',
          type: 'doctor',
          label: t('featureFlags.dsoInvitePractice'),
          isPractice: true,
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'fixedBitrate',
          type: 'doctor',
          label: t('featureFlags.fixedBitrate'),
          isPractice: true,
          availableFor: [Roles.DevAdmin]
        },
        {
          key: 'recordsAppOffline',
          type: 'doctor',
          label: t('featureFlags.recordsAppOffline'),
          isPractice: true,
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'createRecordsOnlyPatients',
          type: 'doctor',
          label: t('featureFlags.createRecordsOnlyPatients'),
          isPractice: true,
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'txTrackerTranslationRotation',
          type: 'doctor',
          label: t('featureFlags.txTrackerTranslationRotation'),
          isPractice: true,
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'mobileOfflineIndicator',
          type: 'doctor',
          isPractice: true,
          label: t('featureFlags.mobileOfflineIndicator'),
          availableFor: [Roles.Admin, Roles.Sales, Roles.Distributor]
        },
        {
          key: 'paginationIssueFF',
          type: 'doctor',
          label: t('featureFlags.paginationIssueFF'),
          availableFor: [Roles.Admin, Roles.Sales, Roles.Distributor]
        },
        {
          key: 'oralHygieneScore',
          type: 'doctor',
          isPractice: true,
          label: t('featureFlags.oralHygieneScore'),
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'biteClassification',
          type: 'doctor',
          isPractice: true,
          label: t('featureFlags.biteClassification'),
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'tagScanForNonHI',
          type: 'doctor',
          isPractice: true,
          label: t('featureFlags.tagScanForNonHI'),
          availableFor: [Roles.Admin, Roles.Sales, Roles.Distributor]
        },
        {
          key: 'recordsAppFocusAreas',
          type: 'doctor',
          isPractice: true,
          label: t('featureFlags.recordsAppFocusAreas'),
          availableFor: [Roles.Admin, Roles.Sales, Roles.Distributor]
        },
        {
          key: 'txTrackerMiniTimeline',
          type: 'doctor',
          isPractice: true,
          label: t('featureFlags.txTrackerMiniTimeline'),
          availableFor: [Roles.DevAdmin]
        },
        {
          key: 'timeBasedMiniTimeline',
          type: 'doctor',
          isPractice: true,
          label: t('featureFlags.timeBasedMiniTimeline'),
          availableFor: [Roles.DevAdmin]
        },
        {
          // Held on the person, not the practice: support sees the time-based view on every
          // patient they open, whatever the practice they are looking at has enabled.
          key: 'timeBasedMiniTimelineSupport',
          type: 'doctor',
          label: t('featureFlags.timeBasedMiniTimelineSupport'),
          availableFor: [Roles.DevAdmin]
        },
        {
          key: 'notificationCenter',
          type: 'doctor',
          isPractice: true,
          label: t('featureFlags.notificationCenter'),
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin, Roles.Admin, Roles.Sales, Roles.Distributor]
        },
        {
          key: 'grinDesignRefresh',
          type: 'doctor',
          isPractice: true,
          label: t('featureFlags.grinDesignRefresh'),
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'copilotControl',
          type: 'doctor',
          isPractice: true,
          label: t('featureFlags.copilotControl'),
          availableFor: [Roles.DevAdmin]
        },
        {
          key: 'oralHygieneReport',
          type: 'doctor',
          isPractice: true,
          label: t('featureFlags.oralHygieneReport'),
          availableFor: [Roles.DevAdmin]
        },
        {
          key: 'txTrackingReport',
          type: 'doctor',
          isPractice: true,
          label: t('featureFlags.txTrackingReport'),
          availableFor: [Roles.DevAdmin]
        },
        {
          key: 'automaticUntaggedPatient',
          type: 'doctor',
          isPractice: true,
          label: t('featureFlags.automaticUntaggedPatient'),
          availableFor: [Roles.DevAdmin]
        },
        {
          key: 'liveCalls',
          type: 'doctor',
          isPractice: true,
          label: t('featureFlags.liveCalls'),
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'addAttachmentsToNote',
          type: 'doctor',
          isPractice: true,
          label: t('featureFlags.addAttachmentsToNote'),
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'AIForHI',
          type: 'doctor',
          label: t('featureFlags.AIForHI'),
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'newMobileDesign',
          type: 'patient',
          label: t('featureFlags.newMobileDesign'),
          availableFor: [Roles.DevAdmin]
        },
        {
          key: 'messageReactions',
          type: 'doctor',
          isPractice: true,
          label: t('featureFlags.messageReactions'),
          availableFor: [Roles.OpsAdmin, Roles.DevAdmin]
        },
        {
          key: 'txTrackerOtherProviders',
          type: 'doctor',
          isPractice: true,
          label: t('featureFlags.txTrackerOtherProviders'),
          availableFor: [Roles.OpsAdmin, Roles.DevAdmin]
        },
        {
          key: 'oralHygieneMobile',
          type: 'doctor',
          isPractice: true,
          label: t('featureFlags.oralHygieneMobile'),
          availableFor: [Roles.OpsAdmin, Roles.DevAdmin]
        },
        {
          key: 'scopeLGOrdering',
          type: 'doctor',
          isPractice: true,
          label: t('featureFlags.scopeLGOrdering'),
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        },
        {
          key: 'payForScopeLG',
          type: 'doctor',
          label: t('featureFlags.payForScopeLG'),
          isPractice: true,
          availableFor: [Roles.DevAdmin, Roles.OpsAdmin]
        }
      ].sort((a, b) => (a.label > b.label ? 1 : -1))
    }),
    [t]
  )
}
